import { useState, useCallback, useRef } from 'react'

/**
 * Hook that manages hover-intent for a trigger + floating element pair.
 * Keeps the floating element open while the mouse is over either the trigger or the element,
 * with a configurable delay before closing.
 *
 * @param {Object} [options]
 * @param {number} [options.closeDelay=200] - Milliseconds to wait before closing after mouse leaves
 * @returns {{
 *   isOpen: boolean,
 *   open: () => void,
 *   close: () => void,
 *   triggerHandlers: { onMouseEnter: Function, onMouseLeave: Function },
 *   floatingHandlers: { onMouseEnter: Function, onMouseLeave: Function },
 * }}
 */
const useHoverIntent = (options = {}) => {
    const { closeDelay = 200 } = options
    const [isOpen, setIsOpen] = useState(false)
    const timeoutRef = useRef(null)

    const clearPendingClose = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [])

    const open = useCallback(() => {
        clearPendingClose()
        setIsOpen(true)
    }, [clearPendingClose])

    const close = useCallback(() => {
        clearPendingClose()
        setIsOpen(false)
    }, [clearPendingClose])

    const scheduleClose = useCallback(() => {
        clearPendingClose()
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false)
        }, closeDelay)
    }, [closeDelay, clearPendingClose])

    const triggerHandlers = {
        onMouseEnter: open,
        onMouseLeave: scheduleClose,
    }

    const floatingHandlers = {
        onMouseEnter: clearPendingClose,
        onMouseLeave: scheduleClose,
    }

    return { isOpen, open, close, triggerHandlers, floatingHandlers }
}

export default useHoverIntent
